import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaCog as FaCogRaw,
  FaHistory as FaHistoryRaw,
  FaPaperPlane as FaPaperPlaneRaw,
  FaPen as FaPenRaw,
  FaRocket as FaRocketRaw,
  FaStar as FaStarRaw,
  FaStream as FaStreamRaw,
  FaTrash as FaTrashRaw,
  FaWpforms as FaWpformsRaw,
  FaUserPlus as FaUserPlusRaw,
  FaUserMinus as FaUserMinusRaw,
} from 'react-icons/fa';
import {
  useCreateMarketingFlow,
  useCreateMarketingFlowStep,
  useDeleteMarketingFlowStep,
  useMarketingSubscriptionChannels,
  useMarketingFlowSteps,
  useMarketingFlows,
  useMarketingKeywords,
  useUpdateMarketingFlow,
  useUpdateMarketingKeyword,
  useUpdateMarketingFlowStep,
} from '../hooks/useMarketing';
import MarketingHeader, { type MarketingHeaderActiveTab } from '../components/MarketingHeader';
import { PrimaryButton } from '../styles/marketingSharedStyles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCog: any = FaCogRaw;
const FaHistory: any = FaHistoryRaw;
const FaPaperPlane: any = FaPaperPlaneRaw;
const FaPen: any = FaPenRaw;
const FaRocket: any = FaRocketRaw;
const FaStar: any = FaStarRaw;
const FaStream: any = FaStreamRaw;
const FaTrash: any = FaTrashRaw;
const FaWpforms: any = FaWpformsRaw;
const FaUserPlus: any = FaUserPlusRaw;
const FaUserMinus: any = FaUserMinusRaw;

const PageWrap = styled.div`
  min-height: 100%;
  background: var(--background);
  display: flex;
  flex-direction: column;
`;


const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 0;
  flex: 1;
  min-height: 0;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const FlowPanel = styled.main`
  padding: 1.5rem 2rem;
  overflow: auto;
`;

const CampaignTitleRow = styled.div`
  margin-bottom: 1.5rem;
`;

const CampaignTitle = styled.h1`
  margin: 0 0 0.25rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--onSurface);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CampaignTitleInput = styled.input`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--onSurface);
  background: var(--surface);
  min-width: 320px;
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const CampaignIcons = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 0.5rem;
  color: var(--onSurfaceVariant);
  cursor: pointer;
  &:hover {
    color: var(--primary);
  }
`;

const FlowColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
`;

const StepConnector = styled.div`
  width: 2px;
  height: 24px;
  background: var(--primary);
  opacity: 0.5;
  margin-left: 20px;
`;

const StepCard = styled.div<{ $selected?: boolean }>`
  position: relative;
  background: var(--surface);
  border: 2px solid ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--outline)')};
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  min-width: 420px;
  max-width: 520px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: border-color 0.15s ease;
`;

const StepCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const StepLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const StepNum = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--primary);
  color: var(--onPrimary);
  font-size: 0.75rem;
`;

const StepActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const ActivePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: var(--success);
  color: var(--onPrimary);
  font-size: 0.7rem;
  font-weight: 600;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  border-radius: 6px;
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
    color: var(--primary);
  }
`;

const MessageBubble = styled.div`
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
  line-height: 1.45;
  margin-bottom: 0.5rem;
`;

const StepMeta = styled.div`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  margin-top: 0.5rem;
`;

const KeywordPill = styled.span`
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: var(--surfaceVariant);
  color: var(--primary);
  font-size: 0.85rem;
  font-weight: 600;
`;

const LogicDesc = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
  line-height: 1.4;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
  cursor: pointer;
  margin-top: 0.5rem;
  input {
    accent-color: var(--primary);
  }
`;

const AddStepBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  margin-left: 20px;
  padding: 0.75rem 1.5rem;
  border: 2px dashed var(--primaryDark);
  background: var(--surfaceVariant);
  color: var(--primary);
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    background: var(--surfaceVariant);
    border-color: var(--primary);
  }
`;

const AddStepMenuWrap = styled.div`
  position: relative;
  display: inline-block;
  margin-top: 1rem;
  margin-left: 20px;
`;

const AddStepDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  min-width: 220px;
  padding: 0.4rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  z-index: 20;
`;

const AddStepDropdownItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  color: var(--onSurface);
  text-align: left;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 0.55rem 0.6rem;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
    color: var(--primary);
  }
`;

const StartOptionRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  margin-left: 0;
  flex-wrap: wrap;
`;

const StartOptionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.7rem 1rem;
  border: 1px solid var(--primaryDark);
  background: var(--surfaceVariant);
  color: var(--primary);
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
    border-color: var(--primary);
  }
`;

const StartOptionHint = styled.p`
  margin: 0.65rem 0 0;
  font-size: 0.84rem;
  color: var(--onSurfaceVariant);
`;

const Sidebar = styled.aside`
  background: var(--surface);
  border-left: 1px solid var(--outline);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow: auto;
  @media (max-width: 1024px) {
    border-left: none;
    border-top: 1px solid var(--outline);
  }
`;

const SidebarTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;


const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
`;

const StatValue = styled.span`
  font-weight: 600;
  color: var(--onSurface);
`;

const StatusDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 0.35rem;
`;

const PhoneMock = styled.div`
  border: 2px solid var(--outline);
  border-radius: 24px;
  padding: 10px;
  background: var(--surface);
  max-width: 260px;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const PhoneScreen = styled.div`
  background: var(--surfaceVariant);
  border-radius: 16px;
  padding: 0.85rem;
  min-height: 280px;
  display: flex;
  flex-direction: column;
`;

const PhoneHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--onSurface);
`;

const OnlineBadge = styled.span`
  font-size: 0.65rem;
  color: var(--success);
  font-weight: 500;
`;

const PreviewBubbleUser = styled.div`
  align-self: flex-end;
  background: var(--primary);
  color: var(--onPrimary);
  padding: 0.5rem 0.75rem;
  border-radius: 12px 12px 4px 12px;
  font-size: 0.8rem;
  max-width: 85%;
  margin-bottom: 0.5rem;
`;

const PreviewBubbleBot = styled.div`
  align-self: flex-start;
  background: var(--surfaceVariant);
  color: var(--onSurfaceVariant);
  padding: 0.5rem 0.75rem;
  border-radius: 12px 12px 12px 4px;
  font-size: 0.8rem;
  line-height: 1.4;
  max-width: 90%;
`;

const PreviewTime = styled.div`
  font-size: 0.65rem;
  color: var(--onSurfaceVariant);
  margin-top: 0.2rem;
`;

const PreviewInput = styled.div`
  margin-top: auto;
  padding-top: 0.5rem;
  font-size: 0.7rem;
  color: var(--onSurfaceVariant);
  border-top: 1px solid var(--outline);
`;

const PreviewDisclaimer = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.7rem;
  color: var(--onSurfaceVariant);
  text-align: center;
`;

const BackLink = styled(Link)`
  color: var(--onSurfaceVariant);
  text-decoration: none;
  font-size: 0.9rem;
  &:hover {
    color: var(--primary);
  }
`;

const EmptyFlow = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--onSurfaceVariant);
  font-size: 0.95rem;
  background: var(--surface);
  border: 2px dashed var(--outline);
  border-radius: 12px;
  margin-bottom: 1rem;
`;

// Step type configs for display
const STEP_TYPES = {
  sms_blast: { label: 'Initial SMS Blast', icon: FaPaperPlane },
  logic_keyword: { label: 'Logic - Trigger Keyword Check', icon: FaStream },
  action_followup: { label: 'Action - Automated Follow-up', icon: FaStar },
  collect_form: { label: 'Action - Collect Form', icon: FaWpforms },
  add_to_list: { label: 'Action - Add to List', icon: FaUserPlus },
  remove_from_list: { label: 'Action - Remove from List', icon: FaUserMinus },
} as const;

type StepTypeKey = keyof typeof STEP_TYPES;

interface StepConfig extends Record<string, unknown> {
  message?: string;
  keyword?: string;
  triggerType?: 'keyword' | 'reply_contains';
  description?: string;
  immediateResponse?: boolean;
  subscriberCount?: number;
  scheduledAt?: string;
  formUrl?: string;
  collectName?: boolean;
  collectEmail?: boolean;
  listChannelId?: string;
  listName?: string;
}

interface SMSFlowPageProps {
  embedded?: boolean;
  flowIdOverride?: string;
  keywordIdOverride?: string;
  flowRouteBase?: string;
  getFlowPath?: (flowId: string, keywordId?: string) => string;
  showHeader?: boolean;
  activeTab?: MarketingHeaderActiveTab;
}

const SidebarCard = styled.section`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1rem;
  background: var(--surface);
`;

const EditorField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const EditorInput = styled.input`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  color: var(--onSurface);
  background: var(--surfaceVariant);
  text-transform: none;
  letter-spacing: normal;
  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--surface);
  }
`;

const EditorTextarea = styled.textarea`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.7rem 0.75rem;
  font-size: 0.9rem;
  color: var(--onSurface);
  min-height: 90px;
  background: var(--surfaceVariant);
  resize: vertical;
  text-transform: none;
  letter-spacing: normal;
  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--surface);
  }
`;

const EditorSelect = styled.select`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  color: var(--onSurface);
  background: var(--surfaceVariant);
  text-transform: none;
  letter-spacing: normal;
  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--surface);
  }
`;

const SmallButton = styled.button`
  border: 1px solid var(--primaryDark);
  background: var(--surfaceVariant);
  color: var(--primary);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
`;

const GhostButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurfaceVariant);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
`;

export default function SMSFlowPage({
  embedded = false,
  flowIdOverride,
  keywordIdOverride,
  flowRouteBase = '/marketing/sms-flow',
  getFlowPath,
  showHeader = true,
  activeTab = 'keywords',
}: SMSFlowPageProps) {
  const { flowId: routeFlowId } = useParams<{ flowId?: string }>();
  const flowId = flowIdOverride ?? routeFlowId;
  const navigate = useNavigate();
  const buildFlowPath = (nextFlowId: string, nextKeywordId?: string) =>
    getFlowPath ? getFlowPath(nextFlowId, nextKeywordId) : `${flowRouteBase}/${nextFlowId}`;
  const flowsQuery = useMarketingFlows();
  const updateKeyword = useUpdateMarketingKeyword();
  const createFlow = useCreateMarketingFlow();
  const updateFlow = useUpdateMarketingFlow();
  const createFlowStep = useCreateMarketingFlowStep();
  const updateFlowStep = useUpdateMarketingFlowStep();
  const deleteFlowStep = useDeleteMarketingFlowStep();
  const keywordsQuery = useMarketingKeywords();
  const channelsQuery = useMarketingSubscriptionChannels();

  const keywords = useMemo(() => keywordsQuery.data ?? [], [keywordsQuery.data]);
  const activeKeyword = useMemo(() => {
    if (keywordIdOverride) return keywords.find((item) => item.id === keywordIdOverride) ?? null;
    if (flowId) return keywords.find((item) => item.flow_id === flowId) ?? null;
    return null;
  }, [flowId, keywordIdOverride, keywords]);
  const effectiveFlowId = activeKeyword?.flow_id ?? flowId;
  const flowStepsQuery = useMarketingFlowSteps(effectiveFlowId ?? undefined);
  const flow = useMemo(
    () => (flowsQuery.data ?? []).find((f) => f.id === effectiveFlowId),
    [flowsQuery.data, effectiveFlowId]
  );
  const steps = useMemo(() => flowStepsQuery.data ?? [], [flowStepsQuery.data]);

  const [campaignName, setCampaignName] = useState('');
  const [campaignDesc, setCampaignDesc] = useState('Design your automated messaging flow');
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null);
  const [stepDraft, setStepDraft] = useState<StepConfig | null>(null);
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [showAddStepOptions, setShowAddStepOptions] = useState(false);
  const addStepMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (flow) {
      setCampaignName(flow.name);
      if (flow.description) setCampaignDesc(flow.description);
    }
  }, [flow]);

  const totalAudience = 5000;
  const estMessages = steps.length > 0 ? Math.round(totalAudience * 1.7) : 0;
  const statusLabel = flow?.status === 'active' ? 'Active' : 'Drafting';
  const statusColor = flow?.status === 'active' ? 'var(--success)' : 'var(--warning)';

  const keywordOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: string[] = [];
    for (const item of keywordsQuery.data ?? []) {
      const value = item.keyword.trim();
      if (!value) continue;
      const key = value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      options.push(value);
    }
    return options;
  }, [keywordsQuery.data]);

  const listNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const channel of channelsQuery.data ?? []) {
      map.set(channel.id, channel.name);
    }
    return map;
  }, [channelsQuery.data]);

  const previewMessages = useMemo(() => {
    const effectiveSteps = steps.map((step) => {
      if (editingStepId && step.id === editingStepId && stepDraft) {
        return { ...step, config: stepDraft };
      }
      return step;
    });
    const messages: Array<{ id: string; sender: 'user' | 'bot'; text: string }> = [];

    for (const step of effectiveSteps) {
      const config = (step.config || {}) as StepConfig;
      if (step.step_type === 'logic_keyword') {
        messages.push({
          id: `${step.id}-trigger`,
          sender: 'user',
          text: config.keyword || 'JOIN',
        });
      }
      if (step.step_type === 'action_followup') {
        messages.push({
          id: `${step.id}-followup`,
          sender: 'bot',
          text: config.message || 'Your automated reply will appear here.',
        });
      }
      if (step.step_type === 'collect_form') {
        messages.push({
          id: `${step.id}-collect-form`,
          sender: 'bot',
          text: config.message || 'Please fill out this form with your name and email to continue.',
        });
      }
      if (step.step_type === 'add_to_list') {
        const listName = config.listChannelId ? listNameById.get(config.listChannelId) ?? '' : config.listName ?? '';
        messages.push({
          id: `${step.id}-add-to-list`,
          sender: 'bot',
          text: config.message || `You have been added to ${listName || 'the subscriber list'}.`,
        });
      }
      if (step.step_type === 'remove_from_list') {
        const listName = config.listChannelId ? listNameById.get(config.listChannelId) ?? '' : config.listName ?? '';
        messages.push({
          id: `${step.id}-remove-from-list`,
          sender: 'bot',
          text: config.message || `You have been removed from ${listName || 'the subscriber list'}.`,
        });
      }
    }

    if (messages.length === 0) {
      const firstBlastStep = effectiveSteps.find((step) => step.step_type === 'sms_blast');
      const blastConfig = (firstBlastStep?.config || {}) as StepConfig;
      messages.push({
        id: 'fallback-outbound',
        sender: 'bot',
        text: blastConfig.message || 'Your first outbound text appears here.',
      });
    }
    return messages;
  }, [editingStepId, listNameById, stepDraft, steps]);

  useEffect(() => {
    if (steps.length > 0 && (selectedStepIndex == null || selectedStepIndex >= steps.length)) {
      setSelectedStepIndex(0);
    }
  }, [steps, selectedStepIndex]);

  useEffect(() => {
    if (!showAddStepOptions) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!addStepMenuRef.current) return;
      if (!addStepMenuRef.current.contains(event.target as Node)) {
        setShowAddStepOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddStepOptions]);

  const handleSaveDraft = async () => {
    if (effectiveFlowId && flow) {
      try {
        await updateFlow.mutateAsync({
          id: effectiveFlowId,
          changes: { name: campaignName.trim() || flow.name, description: campaignDesc || null },
        });
        window.alert('Draft saved.');
      } catch (e) {
        window.alert(e instanceof Error ? e.message : 'Failed to save.');
      }
      return;
    }
    try {
      const newFlow = await createFlow.mutateAsync({
        name: campaignName.trim() || 'Untitled SMS Funnel',
        description: campaignDesc || null,
        status: 'draft',
      });
      if (activeKeyword) {
        await updateKeyword.mutateAsync({ id: activeKeyword.id, changes: { flow_id: newFlow.id } });
      }
      navigate(buildFlowPath(newFlow.id, activeKeyword?.id), { replace: true });
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to create funnel.');
    }
  };

  const handleAddStep = async (stepType: StepTypeKey) => {
    setShowAddStepOptions(false);
    if (!effectiveFlowId) {
      const newFlow = await createFlow.mutateAsync({
        name: campaignName.trim() || 'Untitled SMS Funnel',
        description: campaignDesc || null,
        status: 'draft',
      });
      if (activeKeyword) {
        await updateKeyword.mutateAsync({ id: activeKeyword.id, changes: { flow_id: newFlow.id } });
      }
      navigate(buildFlowPath(newFlow.id, activeKeyword?.id), { replace: true });
      const nextOrder = 1;
      const createdStep = await createFlowStep.mutateAsync({
        flowId: newFlow.id,
        stepOrder: nextOrder,
        stepType,
        config: getDefaultConfig(stepType),
      });
      setEditingStepId(createdStep.id);
      setStepDraft((createdStep.config || {}) as StepConfig);
      setSelectedStepIndex(0);
      return;
    }
    const nextOrder = steps.length + 1;
    try {
      const createdStep = await createFlowStep.mutateAsync({
        flowId: effectiveFlowId,
        stepOrder: nextOrder,
        stepType,
        config: getDefaultConfig(stepType),
      });
      setEditingStepId(createdStep.id);
      setStepDraft((createdStep.config || {}) as StepConfig);
      setSelectedStepIndex(steps.length);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to add step.');
    }
  };

  const handleSaveStepConfig = async (stepId: string) => {
    if (!effectiveFlowId || !stepDraft) return;
    const editingStep = steps.find((step) => step.id === stepId);
    if (
      editingStep &&
      (editingStep.step_type === 'add_to_list' || editingStep.step_type === 'remove_from_list') &&
      !String(stepDraft.listChannelId ?? '').trim()
    ) {
      window.alert('Please choose an existing subscriber list.');
      return;
    }
    try {
      await updateFlowStep.mutateAsync({
        id: stepId,
        flowId: effectiveFlowId,
        changes: { config: stepDraft },
      });
      window.alert('Step updated.');
      setEditingStepId(null);
      setStepDraft(null);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to update step.');
    }
  };

  const handleDeleteStep = async (stepId: string, index: number) => {
    if (!effectiveFlowId) return;
    const confirmed = window.confirm('Delete this step? This cannot be undone.');
    if (!confirmed) return;
    try {
      await deleteFlowStep.mutateAsync({ id: stepId, flowId: effectiveFlowId });
      setShowAddStepOptions(false);
      setEditingStepId((prev) => (prev === stepId ? null : prev));
      setStepDraft((prev) => (editingStepId === stepId ? null : prev));
      setSelectedStepIndex((prev) => {
        if (prev == null) return prev;
        if (prev === index) return null;
        if (prev > index) return prev - 1;
        return prev;
      });
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to delete step.');
    }
  };

  const handleStartInlineStepEdit = (stepId: string, config: StepConfig, index: number) => {
    const nextConfig = { ...config };
    if (!nextConfig.listChannelId && nextConfig.listName) {
      const normalizedName = nextConfig.listName.trim().toLowerCase();
      const matchedChannel = (channelsQuery.data ?? []).find(
        (channel) => channel.name.trim().toLowerCase() === normalizedName
      );
      if (matchedChannel) {
        nextConfig.listChannelId = matchedChannel.id;
        nextConfig.listName = matchedChannel.name;
      }
    }
    setEditingStepId(stepId);
    setStepDraft(nextConfig);
    setSelectedStepIndex(index);
  };

  const handleCancelInlineStepEdit = () => {
    setEditingStepId(null);
    setStepDraft(null);
  };

  function getDefaultConfig(stepType: StepTypeKey): Record<string, unknown> {
    switch (stepType) {
      case 'sms_blast':
        return {
          message: 'Ready for the holidays? Reply JOIN for exclusive VIP early access deals!',
          subscriberCount: 5000,
          scheduledAt: 'Dec 20, 9:00 AM',
        };
      case 'logic_keyword':
        return {
          triggerType: 'keyword',
          keyword: 'JOIN',
          description: 'If subscriber replies with "JOIN", proceed to next step. Otherwise, end funnel.',
        };
      case 'action_followup':
        return {
          message:
            'Welcome to the VIP club! 🎉 Here is your discount code: HOLIDAY24. Valid for all items today only!',
          immediateResponse: true,
        };
      case 'collect_form':
        return {
          message: 'Please fill out this quick form with your name and email: https://example.com/form',
          formUrl: 'https://example.com/form',
          collectName: true,
          collectEmail: true,
        };
      case 'add_to_list':
        return {
          listChannelId: '',
          listName: '',
          message: 'You are now on the VIP Subscribers list. Expect exclusive updates.',
        };
      case 'remove_from_list':
        return {
          listChannelId: '',
          listName: '',
          message: 'You have been removed from the VIP Subscribers list.',
        };
      default:
        return {};
    }
  }

  if (!effectiveFlowId && !createFlow.isPending && flowsQuery.isSuccess) {
    const hasFlows = (flowsQuery.data?.length ?? 0) > 0;
    if (!hasFlows) {
      // No flow selected and no flows yet: show empty state and allow creating from "Add Next Step"
    }
  }

  return (
    <PageWrap>
      {showHeader ? (
        <MarketingHeader
          activeTab={activeTab}
          rightSlot={
            <PrimaryButton type="button" onClick={handleSaveDraft} disabled={createFlow.isPending}>
              Save Draft
            </PrimaryButton>
          }
        />
      ) : null}

      <MainLayout>
        <FlowPanel>
          {embedded ? (
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <PrimaryButton type="button" onClick={handleSaveDraft} disabled={createFlow.isPending}>
                Save Draft
              </PrimaryButton>
            </div>
          ) : (
            <div style={{ marginBottom: '1rem' }}>
              <BackLink to="/marketing/keywords">← Back to Keywords</BackLink>
            </div>
          )}
          <CampaignTitleRow>
            <CampaignTitle>
              <CampaignTitleInput
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Untitled SMS Funnel"
                aria-label="Funnel name"
              />
              <CampaignIcons title="Settings">
                <FaCog size={14} />
              </CampaignIcons>
              <CampaignIcons title="History">
                <FaHistory size={14} />
              </CampaignIcons>
            </CampaignTitle>
            {/* <CampaignDescInput
              type="text"
              value={campaignDesc}
              onChange={(e) => setCampaignDesc(e.target.value)}
              placeholder="Design your automated messaging flow"
              aria-label="Funnel description"
            /> */}
           
          </CampaignTitleRow>

          <FlowColumn>
            {steps.length === 0 && !effectiveFlowId && (
              <EmptyFlow>
                Choose how this funnel starts: your outbound message, or an inbound keyword from the user.
                <StartOptionRow>
                  <StartOptionBtn type="button" onClick={() => handleAddStep('sms_blast')}>
                    Start with Outbound Text
                  </StartOptionBtn>
                  <StartOptionBtn type="button" onClick={() => handleAddStep('logic_keyword')}>
                    Start with Inbound Keyword
                  </StartOptionBtn>
                </StartOptionRow>
                <StartOptionHint>
                  Inbound Keyword starts the funnel when a user texts your keyword first.
                </StartOptionHint>
              </EmptyFlow>
            )}
            {steps.length === 0 && effectiveFlowId && (
              <EmptyFlow>
                No steps yet. Choose a starting step below.
                <StartOptionRow>
                  <StartOptionBtn type="button" onClick={() => handleAddStep('sms_blast')}>
                    Start with Outbound Text
                  </StartOptionBtn>
                  <StartOptionBtn type="button" onClick={() => handleAddStep('logic_keyword')}>
                    Start with Inbound Keyword
                  </StartOptionBtn>
                </StartOptionRow>
                <StartOptionHint>
                  Inbound Keyword starts the funnel when a user texts your keyword first.
                </StartOptionHint>
              </EmptyFlow>
            )}
            {steps.map((step, index) => {
              const typeKey = step.step_type as StepTypeKey;
              const meta = STEP_TYPES[typeKey] ?? { label: step.step_type, icon: FaPaperPlane };
              const config = (step.config || {}) as StepConfig;
              const isSelected = selectedStepIndex === index;
              const isEditing = editingStepId === step.id && stepDraft != null;

              return (
                <React.Fragment key={step.id}>
                  {index > 0 && <StepConnector />}
                  <StepCard $selected={isSelected} onClick={() => setSelectedStepIndex(index)}>
                    <StepCardHeader>
                      <StepLabel>
                        <StepNum>STEP {index + 1}</StepNum>
                        {meta.label}
                      </StepLabel>
                      <StepActions>
                        {step.step_type === 'action_followup' && <ActivePill>ACTIVE</ActivePill>}
                        <IconBtn
                          type="button"
                          aria-label="Edit step"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartInlineStepEdit(step.id, config, index);
                          }}
                        >
                          <FaPen size={12} />
                        </IconBtn>
                        <IconBtn
                          type="button"
                          aria-label="Delete step"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteStep(step.id, index);
                          }}
                          disabled={deleteFlowStep.isPending}
                        >
                          <FaTrash size={12} />
                        </IconBtn>
                      </StepActions>
                    </StepCardHeader>
                    {step.step_type === 'sms_blast' && !isEditing && (
                      <>
                        <MessageBubble>{config.message || 'Your initial SMS message...'}</MessageBubble>
                        <StepMeta>
                          {config.subscriberCount != null
                            ? `${config.subscriberCount.toLocaleString()} subscribers`
                            : '— subscribers'}{' '}
                          · Scheduled: {config.scheduledAt ?? 'Not set'}
                        </StepMeta>
                      </>
                    )}
                    {step.step_type === 'logic_keyword' && !isEditing && (
                      <>
                        <div style={{ marginBottom: '0.5rem' }}>
                          {config.triggerType === 'reply_contains' ? 'Trigger Reply Contains:' : 'Trigger Keyword:'}{' '}
                          <KeywordPill>{config.keyword || 'JOIN'}</KeywordPill>
                        </div>
                        <LogicDesc>
                          {config.description ||
                            'If subscriber replies with the keyword, proceed to next step. Otherwise, end funnel.'}
                        </LogicDesc>
                      </>
                    )}
                    {step.step_type === 'action_followup' && !isEditing && (
                      <>
                        <MessageBubble>{config.message || 'Your automated follow-up message...'}</MessageBubble>
                        <CheckboxLabel>
                          <input
                            type="checkbox"
                            checked={config.immediateResponse ?? true}
                            readOnly
                            aria-hidden
                          />
                          Immediate response
                        </CheckboxLabel>
                      </>
                    )}
                    {step.step_type === 'collect_form' && !isEditing && (
                      <>
                        <MessageBubble>{config.message || 'Send a form link to collect name and email.'}</MessageBubble>
                        <StepMeta>
                          Collects:{' '}
                          {[
                            config.collectName !== false ? 'Name' : null,
                            config.collectEmail !== false ? 'Email' : null,
                          ]
                            .filter(Boolean)
                            .join(', ') || 'None'}{' '}
                          · Form URL: {config.formUrl || 'Not set'}
                        </StepMeta>
                      </>
                    )}
                    {step.step_type === 'add_to_list' && !isEditing && (
                      <>
                        <MessageBubble>{config.message || 'Adds subscriber to a list.'}</MessageBubble>
                        <StepMeta>
                          List:{' '}
                          {(config.listChannelId ? listNameById.get(config.listChannelId) : null) ||
                            config.listName ||
                            'Not set'}
                        </StepMeta>
                      </>
                    )}
                    {step.step_type === 'remove_from_list' && !isEditing && (
                      <>
                        <MessageBubble>{config.message || 'Removes subscriber from a list.'}</MessageBubble>
                        <StepMeta>
                          List:{' '}
                          {(config.listChannelId ? listNameById.get(config.listChannelId) : null) ||
                            config.listName ||
                            'Not set'}
                        </StepMeta>
                      </>
                    )}
                    {isEditing && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                        {step.step_type === 'sms_blast' && (
                          <EditorField>
                            Initial message
                            <EditorTextarea
                              value={stepDraft.message ?? ''}
                              onChange={(e) => setStepDraft((prev) => ({ ...(prev || {}), message: e.target.value }))}
                              placeholder="Write the opening message..."
                            />
                          </EditorField>
                        )}
                        {step.step_type === 'logic_keyword' && (
                          <>
                            <EditorField>
                              Trigger type
                              <EditorSelect
                                value={stepDraft.triggerType ?? 'keyword'}
                                onChange={(e) =>
                                  setStepDraft((prev) => ({
                                    ...(prev || {}),
                                    triggerType: e.target.value as StepConfig['triggerType'],
                                  }))
                                }
                              >
                                <option value="keyword">Exact keyword</option>
                                <option value="reply_contains">Reply contains</option>
                              </EditorSelect>
                            </EditorField>
                            <EditorField>
                              {stepDraft.triggerType === 'reply_contains' ? 'Reply text to match' : 'Keyword'}
                              <>
                                <EditorSelect
                                  value={
                                    (stepDraft.keyword ?? '').trim() &&
                                    keywordOptions.some(
                                      (option) =>
                                        option.toLowerCase() === (stepDraft.keyword ?? '').trim().toLowerCase()
                                    )
                                      ? (stepDraft.keyword ?? '').trim()
                                      : '__custom__'
                                  }
                                  onChange={(e) => {
                                    const nextValue = e.target.value;
                                    if (nextValue === '__custom__') {
                                      setStepDraft((prev) => ({ ...(prev || {}), keyword: '' }));
                                      return;
                                    }
                                    setStepDraft((prev) => ({ ...(prev || {}), keyword: nextValue }));
                                  }}
                                >
                                  <option value="">
                                    {stepDraft.triggerType === 'reply_contains' ? 'Select reply keyword' : 'Select keyword'}
                                  </option>
                                  {keywordOptions.map((keyword) => (
                                    <option key={keyword} value={keyword}>
                                      {keyword}
                                    </option>
                                  ))}
                                  <option value="__custom__">Custom keyword…</option>
                                </EditorSelect>
                                {(((stepDraft.keyword ?? '').trim() === '' ||
                                  !keywordOptions.some(
                                    (option) => option.toLowerCase() === (stepDraft.keyword ?? '').trim().toLowerCase()
                                  )) && (
                                  <EditorInput
                                    value={stepDraft.keyword ?? ''}
                                    onChange={(e) =>
                                      setStepDraft((prev) => ({ ...(prev || {}), keyword: e.target.value }))
                                    }
                                    placeholder={
                                      stepDraft.triggerType === 'reply_contains'
                                        ? 'Type custom reply keyword'
                                        : 'Type custom keyword'
                                    }
                                  />
                                )) || null}
                              </>
                            </EditorField>
                            <EditorField>
                              Rule notes
                              <EditorTextarea
                                value={stepDraft.description ?? ''}
                                onChange={(e) => setStepDraft((prev) => ({ ...(prev || {}), description: e.target.value }))}
                                placeholder="Describe what happens next..."
                              />
                            </EditorField>
                          </>
                        )}
                        {step.step_type === 'action_followup' && (
                          <>
                            <EditorField>
                              Custom auto reply
                              <EditorTextarea
                                value={stepDraft.message ?? ''}
                                onChange={(e) => setStepDraft((prev) => ({ ...(prev || {}), message: e.target.value }))}
                                placeholder="Write your automated response..."
                              />
                            </EditorField>
                            <CheckboxLabel style={{ marginTop: 0 }}>
                              <input
                                type="checkbox"
                                checked={stepDraft.immediateResponse ?? true}
                                onChange={(e) =>
                                  setStepDraft((prev) => ({
                                    ...(prev || {}),
                                    immediateResponse: e.target.checked,
                                  }))
                                }
                              />
                              Send immediately after trigger
                            </CheckboxLabel>
                          </>
                        )}
                        {step.step_type === 'collect_form' && (
                          <>
                            <EditorField>
                              Form invite message
                              <EditorTextarea
                                value={stepDraft.message ?? ''}
                                onChange={(e) => setStepDraft((prev) => ({ ...(prev || {}), message: e.target.value }))}
                                placeholder="Write the SMS that asks the user to fill out the form..."
                              />
                            </EditorField>
                            <EditorField>
                              Form URL
                              <EditorInput
                                value={stepDraft.formUrl ?? ''}
                                onChange={(e) => setStepDraft((prev) => ({ ...(prev || {}), formUrl: e.target.value }))}
                                placeholder="https://example.com/form"
                              />
                            </EditorField>
                            <CheckboxLabel style={{ marginTop: 0 }}>
                              <input
                                type="checkbox"
                                checked={stepDraft.collectName ?? true}
                                onChange={(e) =>
                                  setStepDraft((prev) => ({
                                    ...(prev || {}),
                                    collectName: e.target.checked,
                                  }))
                                }
                              />
                              Collect Name
                            </CheckboxLabel>
                            <CheckboxLabel style={{ marginTop: 0 }}>
                              <input
                                type="checkbox"
                                checked={stepDraft.collectEmail ?? true}
                                onChange={(e) =>
                                  setStepDraft((prev) => ({
                                    ...(prev || {}),
                                    collectEmail: e.target.checked,
                                  }))
                                }
                              />
                              Collect Email
                            </CheckboxLabel>
                          </>
                        )}
                        {step.step_type === 'add_to_list' && (
                          <>
                            <EditorField>
                              Subscriber list
                              <EditorSelect
                                value={stepDraft.listChannelId ?? ''}
                                onChange={(e) => {
                                  const channelId = e.target.value;
                                  const channelName = channelId ? listNameById.get(channelId) ?? '' : '';
                                  setStepDraft((prev) => ({
                                    ...(prev || {}),
                                    listChannelId: channelId,
                                    listName: channelName,
                                  }));
                                }}
                              >
                                <option value="">Select existing list</option>
                                {(channelsQuery.data ?? []).map((channel) => (
                                  <option key={channel.id} value={channel.id}>
                                    {channel.name}
                                  </option>
                                ))}
                              </EditorSelect>
                            </EditorField>
                            <EditorField>
                              Confirmation message
                              <EditorTextarea
                                value={stepDraft.message ?? ''}
                                onChange={(e) => setStepDraft((prev) => ({ ...(prev || {}), message: e.target.value }))}
                                placeholder="Tell subscriber they were added to the list..."
                              />
                            </EditorField>
                            {!(channelsQuery.data ?? []).length && (
                              <StepMeta>No subscriber lists found yet. Create one in Keyword Management first.</StepMeta>
                            )}
                          </>
                        )}
                        {step.step_type === 'remove_from_list' && (
                          <>
                            <EditorField>
                              Subscriber list
                              <EditorSelect
                                value={stepDraft.listChannelId ?? ''}
                                onChange={(e) => {
                                  const channelId = e.target.value;
                                  const channelName = channelId ? listNameById.get(channelId) ?? '' : '';
                                  setStepDraft((prev) => ({
                                    ...(prev || {}),
                                    listChannelId: channelId,
                                    listName: channelName,
                                  }));
                                }}
                              >
                                <option value="">Select existing list</option>
                                {(channelsQuery.data ?? []).map((channel) => (
                                  <option key={channel.id} value={channel.id}>
                                    {channel.name}
                                  </option>
                                ))}
                              </EditorSelect>
                            </EditorField>
                            <EditorField>
                              Confirmation message
                              <EditorTextarea
                                value={stepDraft.message ?? ''}
                                onChange={(e) => setStepDraft((prev) => ({ ...(prev || {}), message: e.target.value }))}
                                placeholder="Tell subscriber they were removed from the list..."
                              />
                            </EditorField>
                            {!(channelsQuery.data ?? []).length && (
                              <StepMeta>No subscriber lists found yet. Create one in Keyword Management first.</StepMeta>
                            )}
                          </>
                        )}
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <SmallButton
                            type="button"
                            onClick={() => handleSaveStepConfig(step.id)}
                            disabled={updateFlowStep.isPending || !effectiveFlowId}
                          >
                            {updateFlowStep.isPending ? 'Saving…' : 'Save Step'}
                          </SmallButton>
                          <GhostButton type="button" onClick={handleCancelInlineStepEdit} disabled={updateFlowStep.isPending}>
                            Cancel
                          </GhostButton>
                        </div>
                      </div>
                    )}
                  </StepCard>
                </React.Fragment>
              );
            })}
            {(steps.length > 0 || (effectiveFlowId && steps.length > 0)) && (
              <>
                <StepConnector />
                <AddStepMenuWrap ref={addStepMenuRef}>
                  <AddStepBtn
                    type="button"
                    onClick={() => setShowAddStepOptions((prev) => !prev)}
                    disabled={createFlow.isPending || createFlowStep.isPending}
                    style={{ marginTop: 0, marginLeft: 0 }}
                  >
                    + Add Next Step
                  </AddStepBtn>
                  {showAddStepOptions && (
                    <AddStepDropdown>
                      <AddStepDropdownItem
                        type="button"
                        onClick={() => handleAddStep('logic_keyword')}
                        disabled={createFlow.isPending || createFlowStep.isPending}
                      >
                        Keyword Trigger
                      </AddStepDropdownItem>
                      <AddStepDropdownItem
                        type="button"
                        onClick={() => handleAddStep('action_followup')}
                        disabled={createFlow.isPending || createFlowStep.isPending}
                      >
                        Follow-up
                      </AddStepDropdownItem>
                      <AddStepDropdownItem
                        type="button"
                        onClick={() => handleAddStep('collect_form')}
                        disabled={createFlow.isPending || createFlowStep.isPending}
                      >
                        Collect Form
                      </AddStepDropdownItem>
                      <AddStepDropdownItem
                        type="button"
                        onClick={() => handleAddStep('add_to_list')}
                        disabled={createFlow.isPending || createFlowStep.isPending}
                      >
                        Add to List
                      </AddStepDropdownItem>
                      <AddStepDropdownItem
                        type="button"
                        onClick={() => handleAddStep('remove_from_list')}
                        disabled={createFlow.isPending || createFlowStep.isPending}
                      >
                        Remove from List
                      </AddStepDropdownItem>
                    </AddStepDropdown>
                  )}
                </AddStepMenuWrap>
              </>
            )}
          </FlowColumn>

        </FlowPanel>

        <Sidebar>
          <SidebarCard>
            <SidebarTitle>Live Mobile Preview</SidebarTitle>
            <PhoneMock>
              <PhoneScreen>
                <PhoneHeader>
                  VIP CLUB <OnlineBadge>Online</OnlineBadge>
                </PhoneHeader>
                {previewMessages.map((message, index) =>
                  message.sender === 'user' ? (
                    <PreviewBubbleUser key={message.id}>{message.text}</PreviewBubbleUser>
                  ) : (
                    <PreviewBubbleBot key={message.id}>
                      {message.text}
                      {index === previewMessages.length - 1 && <PreviewTime>Just now</PreviewTime>}
                    </PreviewBubbleBot>
                  )
                )}
                <PreviewInput>iMessage</PreviewInput>
              </PhoneScreen>
            </PhoneMock>
            <PreviewDisclaimer>
              This is an approximate preview of the flow conversation.
            </PreviewDisclaimer>
          </SidebarCard>

          <SidebarCard>
          <SidebarTitle>Campaign Controls</SidebarTitle>
          <PrimaryButton type="button" style={{ width: '100%' }} disabled={!effectiveFlowId}>
            <FaRocket size={14} /> Launch Campaign
          </PrimaryButton>
          
          <StatRow>
            <span>Total Audience</span>
            <StatValue>{totalAudience.toLocaleString()}</StatValue>
          </StatRow>
          <StatRow>
            <span>Est. Messages</span>
            <StatValue>~{estMessages.toLocaleString()}</StatValue>
          </StatRow>
          <StatRow>
            <span>Status</span>
            <StatValue>
              <StatusDot $color={statusColor} />
              {statusLabel}
            </StatValue>
          </StatRow>
          </SidebarCard>
        </Sidebar>
      </MainLayout>
    </PageWrap>
  );
}
